import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import gsap from 'gsap';

const Hero = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.hero-element', {
                y: 40,
                opacity: 0,
                duration: 1.2,
                stagger: 0.15,
                ease: 'power3.out',
                delay: 0.2
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={containerRef}
            className="relative min-h-[100dvh] h-auto w-full flex items-end pt-40 md:pt-44 xl:pt-36 pb-16 md:pb-20 xl:pb-28 px-6 md:px-16 overflow-hidden bg-[#0A0A14]"
        >
            {/* Background Image & Overlays */}
            <div className="absolute inset-0 w-full h-full z-0">
                <img
                    src="/images/generated/hero_roofing_command.png"
                    alt="Roofing contractor command center powered by autonomous AI"
                    className="w-full h-full object-cover object-center opacity-80 hero-element"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-[#0A0A14]/60 to-transparent"></div>
                <div className="absolute inset-0 bg-[#0A0A14]/30 mix-blend-multiply"></div>
            </div>

            {/* Content — customer as hero, RoofClaw as guide */}
            <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col md:flex-row items-end justify-between gap-12">
                <div className="flex flex-col items-start max-w-3xl">

                    {/* Avatar eyebrow — speaks directly to the customer */}
                    <p className="hero-element text-[#7B61FF] font-data tracking-widest text-xs mb-6 uppercase flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#7B61FF] animate-pulse"></span>
                        Built for Storm Restoration Roofers
                    </p>

                    {/* Hero headline — customer desire, not brand claim */}
                    <h1 className="flex flex-col text-[#F0EFF4] leading-[1.05] mb-8">
                        <span className="hero-element font-bold text-[clamp(3rem,6vw,5.5rem)] tracking-[-0.04em]">
                            Your next storm surge
                        </span>
                        <span className="hero-element font-drama text-[clamp(4.25rem,8vw,7.75rem)] text-[#7B61FF] -mt-2 md:-mt-3">
                            shouldn't break your office.
                        </span>
                    </h1>

                    {/* Subheader — the guide's pitch, not the hero's brag */}
                    <p className="hero-element text-[#F0EFF4]/70 text-lg md:text-xl font-light mb-10 max-w-xl leading-relaxed">
                        RoofClaw deploys an autonomous AI Chief of Staff directly onto your own Apple hardware — pre-configured, zero-trust secured, and shipped to your desk. You plug it in. It handles the chaos.
                    </p>

                    {/* Dual CTA: Direct + Transitional */}
                    <div className="hero-element flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <Link
                                to="/pricing"
                                className="btn-magnetic bg-[#7B61FF] text-[#F0EFF4] px-8 py-4 rounded-full font-semibold tracking-wide shadow-[0_0_20px_rgba(123,97,255,0.4)] hover:scale-[1.03] transition-transform"
                            >
                                See Pricing →
                            </Link>
                            <a
                                href="#features"
                                className="btn-magnetic border border-[#F0EFF4]/20 text-[#F0EFF4]/80 px-8 py-4 rounded-full font-medium hover:bg-[#F0EFF4]/10 backdrop-blur-sm transition-all"
                            >
                                See How It Works ↓
                            </a>
                        </div>

                        {/* Call Vito — Phone Agent CTA */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-1">
                            <a
                                href="tel:+17754366049"
                                className="group btn-magnetic flex items-center gap-3 border border-[#7B61FF]/40 text-[#F0EFF4]/90 px-6 py-3 rounded-full font-medium hover:border-[#7B61FF] hover:shadow-[0_0_16px_rgba(123,97,255,0.35)] backdrop-blur-sm transition-all duration-300"
                                aria-label="Call Vito at +1 (775) 436-6049"
                            >
                                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#7B61FF]/20 group-hover:bg-[#7B61FF]/40 transition-colors duration-300">
                                    <Phone className="w-3.5 h-3.5 text-[#7B61FF]" strokeWidth={2.5} />
                                </span>
                                <span className="flex flex-col leading-tight">
                                    <span className="text-xs text-[#7B61FF] font-data tracking-widest uppercase">Call Vito →</span>
                                    <span className="text-sm font-semibold tracking-wide">+1 (775) 436-6049</span>
                                </span>
                            </a>
                            <span className="flex items-center gap-1.5 text-[#F0EFF4]/40 text-xs font-data">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse"></span>
                                AI agent answers live, 24/7
                            </span>
                        </div>

                        <p className="text-[#F0EFF4]/40 text-xs font-data leading-relaxed max-w-lg hidden md:block">
                            * Two ways in — full build or $800 Readiness Session.<br />
                            ** Free shipping included. Continental US & Canada.
                        </p>
                    </div>
                </div>

                {/* Proof signal — top-right atmospheric counter */}
                <div className="hero-element hidden md:flex flex-col items-end gap-3 text-right shrink-0">
                    <div className="font-data text-[#F0EFF4]/40 text-xs tracking-widest">SYS.STATUS</div>
                    <div className="font-data text-[#7B61FF] text-sm">SECURE_TUNNEL_ACTIVE</div>
                    <div className="w-16 h-[1px] bg-[#7B61FF]/50 mt-1"></div>
                    <div className="font-data text-[#F0EFF4]/30 text-[10px] leading-relaxed mt-2">
                        500M+ TOKENS BURNED<br />
                        ZERO DATA BREACHES<br />
                        ★ 5.0 FROM CONTRACTORS
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
